import { HazardBaseLayer, CustomSymbolizer } from "./HazardBaseLayer";
import {
  getHazardLayerProps,
  type HazardLayerConfig,
} from "@/features/map/utils/hazardLayerUtils";
import type { Feature, PaintRule } from "protomaps-leaflet";

interface Props {
  year: number;
  defaultVisible: boolean;
  visible?: boolean;
}

const host = process.env.VITE_HAZARD_TILE_HOST ?? "";

export const LANDSLIDE_STEEP_SLOPE_LAYER_NAME = "土砂災害（急傾斜地の崩壊）";
export const LANDSLIDE_DEBRIS_FLOW_LAYER_NAME = "土砂災害（土石流）";
export const LANDSLIDE_LANDSLIDE_LAYER_NAME = "土砂災害（地すべり）";

// 区域区分 (A33_002) を現象種別ごとに色分け
// 1: 警戒区域, 2: 特別警戒区域, 3/4: 指定前（指定前は警戒/特別と同系色でフォールバック）
const steepSlopeColorMap: Record<string, string> = {
  "1": "#FFED4C",
  "2": "#FB684C",
  "3": "#FFED4C",
  "4": "#FB684C",
};
const debrisFlowColorMap: Record<string, string> = {
  "1": "#EDD86F",
  "2": "#C04C63",
  "3": "#EDD86F",
  "4": "#C04C63",
};
const landslideColorMap: Record<string, string> = {
  "1": "#FFB74C",
  "2": "#CA4C95",
  "3": "#FFB74C",
  "4": "#CA4C95",
};
const zoneAttributeKey = "A33_002";
const phenomenonFilterKey = "A33_001";

/*
土砂災害警戒区域レイヤー（A33）を現象種別ごとに分割
タイル元データ: 土砂災害警戒区域データ 2022/2023年度 (A33-22_00) / local-tiles/a33/a33_all.pmtiles
*/
const createPhenomenonLayer = (
  phenomenonCode: string,
  displayName: string,
  layerName: string,
  colorMap: Record<string, string>
): React.FC<Props> => {
  const symbolizerOpacity = 1;
  const phenomenonFilter = (_zoom: number, feature: Feature): boolean =>
    String(feature.props[phenomenonFilterKey] ?? "") === phenomenonCode;

  const Component: React.FC<Props> = ({ year, defaultVisible, visible }) => {
    const layerConfig: HazardLayerConfig = {
      layerName,
      yearConfigs: {
        2024: {
          url: `${host}/33/landslide_33.pmtiles`,
          dataLayer: "a33_polygon",
        },
        2025: {
          url: `${host}/33/a33-2025_z14_z19_compliant.pmtiles`,
          dataLayer: "a33_polygon",
        },
      },
    };

    const layerProps = getHazardLayerProps(year, layerConfig);

    if (!layerProps) return null;

    const { url, dataLayer: configDataLayer } = layerProps;

    const paintRules: PaintRule[] = [
      {
        dataLayer: configDataLayer,
        minzoom: 14,
        maxzoom: 22,
        filter: phenomenonFilter,
        symbolizer: new CustomSymbolizer(
          zoneAttributeKey,
          colorMap,
          zoneAttributeKey,
          ["1", "3"],
          symbolizerOpacity
        ),
      },
      {
        dataLayer: configDataLayer,
        minzoom: 14,
        maxzoom: 22,
        filter: phenomenonFilter,
        symbolizer: new CustomSymbolizer(
          zoneAttributeKey,
          colorMap,
          zoneAttributeKey,
          ["2", "4"],
          symbolizerOpacity
        ),
      },
    ];

    return (
      <HazardBaseLayer
        defaultVisible={defaultVisible}
        layerName={layerProps.layerName}
        url={url}
        attributeKey={zoneAttributeKey}
        colorMap={colorMap}
        paintRules={paintRules}
        dataLayer={configDataLayer}
        minzoom={14}
        maxzoom={22}
        opacity={0.7}
        visible={visible}
      />
    );
  };
  Component.displayName = displayName;
  return Component;
};

export const HazardLandslideSteepSlopeLayer = createPhenomenonLayer(
  "1",
  "HazardLandslideSteepSlopeLayer",
  LANDSLIDE_STEEP_SLOPE_LAYER_NAME,
  steepSlopeColorMap
);
export const HazardLandslideDebrisFlowLayer = createPhenomenonLayer(
  "2",
  "HazardLandslideDebrisFlowLayer",
  LANDSLIDE_DEBRIS_FLOW_LAYER_NAME,
  debrisFlowColorMap
);
export const HazardLandslideLandslideLayer = createPhenomenonLayer(
  "3",
  "HazardLandslideLandslideLayer",
  LANDSLIDE_LANDSLIDE_LAYER_NAME,
  landslideColorMap
);
